import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import React from 'react';

import { Button } from '@/components/_molecules/Button';
import {
  ProductQuantityProvider,
  useProductQuantity,
} from '@/components/_molecules/ProductListingActions/ProductQuantityProvider';
import { Select } from '@/components/_molecules/Select';
import { useAddToCheckout } from '@/logic/checkout/useAddToCheckout';
import { ApiErrorCodes, isApiError } from '@/network/api.utils';
import { cn } from '@/utils/styles.utils';

import { useModals } from '../Modals';

import { useProductQuantityOptions } from './useProductQuantityOptions';

interface Props {
  sku: string;
  size?: 's' | 'm';
  bigButton?: boolean;
}

export const ProductListingActions = (props: Props & { maxQuantity: number }) => {
  const { maxQuantity, ...rest } = props;
  return (
    <ProductQuantityProvider maxQuantity={maxQuantity}>
      <ProductListingActionsBasic {...rest} />
    </ProductQuantityProvider>
  );
};

export const ProductListingActionsBasic = (props: Props) => {
  const t = useTranslations();
  const { openInfoModal } = useModals();
  const router = useRouter();
  const { sku, size = 'm', bigButton = false } = props;
  const { quantity, onChangeQuantity, maxQuantity } = useProductQuantity();
  const productQuantityOptions = useProductQuantityOptions(maxQuantity);
  const addToCheckout = useAddToCheckout();
  const handleClick = async () => {
    try {
      await addToCheckout({
        sku,
        quantity: Number(quantity),
      });
      router.push('/koszyk');
    } catch (err) {
      if (isApiError(err) && err.data.error_code === ApiErrorCodes.createUpdateCheckout.VARIANT_SOLD_OUT) {
        openInfoModal({
          title: t('createCheckout.VARIANT_SOLD_OUT.title'),
          description: t('createCheckout.VARIANT_SOLD_OUT.description'),
          label: t('createCheckout.VARIANT_SOLD_OUT.label'),
          onConfirm: () => router.reload(),
        });
      } else {
        throw err;
      }
    }
  };

  return (
    <div
      className={cn('grid w-full grid-cols-[1fr_1.1fr] gap-2 laptop:grid-cols-[1fr_1.6fr]', {
        'grid-cols-[1fr_1.5fr]': bigButton,
      })}
    >
      <Select
        size={size}
        options={productQuantityOptions.options}
        value={String(quantity)}
        onChange={onChangeQuantity}
        allowNull={false}
        label="Ilość"
      />
      <Button textId="productBox.buttonAddToBasket" variant="primary" size={size} onClick={handleClick} />
    </div>
  );
};
